import { __spreadArray } from "tslib";
import { DebounceHelper } from './DebounceHelper';
import { ensureTypeInfo } from './Model/NeoTypeInfo';
var lut = [];
for (var i = 0; i < 256; i++) {
    lut[i] = (i < 16 ? '0' : '') + (i).toString(16);
}
var Utils = /** @class */ (function () {
    function Utils() {
    }
    /**
     * Calls the supplied function once all computeds have been evaluated.
     * In Vue this is asyncronous, in React the callback is executed syncronously.
     */
    Utils.nextTick = function (callback) {
        callback();
    };
    /**
     * Reads an observable value without registering a dependency.
     */
    Utils.peek = function (target, propertyName) {
        throw "missing peek implementation.";
    };
    /**
     * Returns a new guid.
     */
    Utils.uuidv4 = function () {
        var d0 = Math.random() * 0xffffffff | 0;
        var d1 = Math.random() * 0xffffffff | 0;
        var d2 = Math.random() * 0xffffffff | 0;
        var d3 = Math.random() * 0xffffffff | 0;
        return lut[d0 & 0xff] + lut[d0 >> 8 & 0xff] + lut[d0 >> 16 & 0xff] + lut[d0 >> 24 & 0xff] + '-' +
            lut[d1 & 0xff] + lut[d1 >> 8 & 0xff] + '-' + lut[d1 >> 16 & 0x0f | 0x40] + lut[d1 >> 24 & 0xff] + '-' +
            lut[d2 & 0x3f | 0x80] + lut[d2 >> 8 & 0xff] + '-' + lut[d2 >> 16 & 0xff] + lut[d2 >> 24 & 0xff] +
            lut[d3 & 0xff] + lut[d3 >> 8 & 0xff] + lut[d3 >> 16 & 0xff] + lut[d3 >> 24 & 0xff];
    };
    /**
     * Returns an empty guid.
     */
    Utils.emptyGuid = function () {
        return "00000000-0000-0000-0000-000000000000";
    };
    /**
     * Runs the callback in the context of a batch transaction. E.g. a mobx action in react.
     */
    Utils.batchUpdate = function (unitOfWork) {
        return unitOfWork();
    };
    /**
     * Returns an array that can be sorted using the built in Array.sort method.
     * Mobx dissallows in place sorting on observable arrays.
     */
    Utils.sortableArray = function (array) {
        return array;
    };
    /**
     * Instructs the ui not to encode the provided html text.
     * Returns a new element containing the html.
     */
    Utils.suppressHtmlEncode = function (html) {
        // This method must be overridden by the ui framework.
        return html;
    };
    Utils.XOR = function (condition1, condition2) {
        return (condition1 && !condition2) || (!condition1 && condition2);
    };
    Utils.sameTypes = function (type1, type2) {
        if (!!type1 && !!type2) {
            return type1.prototype.constructor == type2.prototype.constructor;
        }
        return false;
    };
    Utils.getTypeName = function (type) {
        return type.prototype.constructor.name;
    };
    /**
     * Creates an instance of a model decorated with @NeoModel. Use this in static methods on a model class that is compiled with babel (React projects).
     * @param type Type to create.
     * @param args Constructor args.
     */
    Utils.createInstance = function (type) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        if (type.prototype.constructor.neo) {
            return ensureTypeInfo(type).neo.createInstance(args);
        }
        else {
            throw "Cannot instantiate '".concat(type.name, "' because it does not have an @NeoModel attribute.");
        }
    };
    /**
     * Binds all the methods of an instances prototype to the instance.
     * This guarantees that 'this' will be the instance in each method.
     * @param instance Instance to process.
     */
    Utils.bindAllMethods = function (instance) {
        var prototype = instance.constructor.prototype;
        for (var _i = 0, _a = Object.getOwnPropertyNames(prototype); _i < _a.length; _i++) {
            var key = _a[_i];
            if (key !== "constructor" && prototype[key] instanceof Function && !instance.hasOwnProperty(key)) {
                // @ts-ignore
                instance[key] = prototype[key].bind(instance);
            }
        }
    };
    /**
     * Completes a partially populated object using defaults specified in the defaults parameter.
     * @param partial Partially populated object
     * @param defaults Defaults object.
     * @param force True if the properties in `partial` must be set even if they have a value.
     */
    Utils.populatePartialWithDefaults = function (partial, defaults, force) {
        if (force === void 0) { force = false; }
        if (!partial)
            partial = {};
        for (var key in defaults) {
            if (force || !partial[key])
                partial[key] = defaults[key];
        }
        return partial;
    };
    /**
     * Converts a string like 'user' to 'fa fa-user' or 'fa-user' to 'fa fa-user' or 'far-user' to 'far fa-user'
     */
    Utils.getFontAwesomeClass = function (faString) {
        var props = this.getFontAwesomeIconProps(faString);
        return (props.isSolid === false ? 'far ' : (props.isSolid === true ? 'fas ' : 'fa ')) + 'fa-' + props.name;
    };
    Utils.getFontAwesomeIconProps = function (faString) {
        var isSolid;
        if (faString.indexOf("fa-") === 0) {
            faString = faString.substring(3);
        }
        else if (faString.indexOf("far-") === 0) {
            isSolid = false;
            faString = faString.substring(4);
        }
        else if (faString.indexOf("fas-") === 0) {
            isSolid = true;
            faString = faString.substring(4);
        }
        return { name: faString, isSolid: isSolid };
    };
    /** Concatenates an array of strings together, putting spaces in-between them. */
    Utils.joinWithSpaces = function (arg0) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        if (!arg0)
            arg0 = "";
        for (var _a = 0, args_1 = args; _a < args_1.length; _a++) {
            var arg = args_1[_a];
            if (arg) {
                arg0 += (arg0 ? " " : "") + arg;
            }
        }
        return arg0;
    };
    /**
     * Debounce a function so that it is only called after a period of inactivity.
     * @param syncInstance The instance to synchronise against. This should always be the same object for the function you are debouncing.
     * @param fn Function to call when the delay has elapsed.
     * @param delayMs Delay in milliseconds.
     * @param state State to pass to debounced method as an array of values for each time debounce was called.
     */
    Utils.debounce = function (syncInstance, callback, delayMs, state) {
        return DebounceHelper.debounce(syncInstance, callback, delayMs, state);
    };
    /** Cancels pending execution of the callback. */
    Utils.clearDebounce = function (syncInstance) {
        return DebounceHelper.cancelPending(syncInstance);
    };
    /**
     * Gets a list of property values in query string format. Does not include the leading ?.
     * @param properties Optional list of property names to include in the query string.
     */
    Utils.getQueryString = function (plainObject) {
        var properties = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            properties[_i - 1] = arguments[_i];
        }
        function buildQueryString(plainObject, prefix) {
            var properties = [];
            for (var _i = 2; _i < arguments.length; _i++) {
                properties[_i - 2] = arguments[_i];
            }
            var queryString = "";
            var propertyNames = properties.length === 0 ? Object.keys(plainObject) : properties;
            for (var _a = 0, propertyNames_1 = propertyNames; _a < propertyNames_1.length; _a++) {
                var propertyKey = propertyNames_1[_a];
                // @ts-ignore
                var value = plainObject[propertyKey];
                if (value !== undefined && value !== null && value !== "") {
                    var name_1 = prefix ? "".concat(prefix, ".").concat(propertyKey) : propertyKey;
                    var append = "";
                    if (value instanceof Date) {
                        value = value.toISOString();
                    }
                    else if (value instanceof Array && value.length) {
                        if (value[0] instanceof Date) {
                            for (var i_1 = 0; i_1 < value.length; i_1++) {
                                append += (i_1 === 0 ? "" : "&") + "".concat(name_1, "=").concat(value[i_1].toISOString());
                            }
                        }
                        else if (value[0] instanceof Object) {
                            // Object arrays are in the form: sortColumns[0].columnName=Prop1&sortColumns[1].columnName=Prop4
                            for (var i_2 = 0; i_2 < value.length; i_2++) {
                                append += (i_2 === 0 ? "" : "&") + buildQueryString(value[i_2], "".concat(name_1, "[").concat(i_2, "]"));
                            }
                        }
                        else {
                            // Plain arrays are in the form: values=1&values=2
                            for (var i_3 = 0; i_3 < value.length; i_3++) {
                                append += (i_3 === 0 ? "" : "&") + "".concat(name_1, "=").concat(encodeURIComponent(value[i_3]));
                            }
                        }
                    }
                    else if (value instanceof Object) {
                        append = buildQueryString(value, name_1);
                        if (!append)
                            continue;
                    }
                    if (queryString !== "") {
                        queryString += "&";
                    }
                    queryString += append || (name_1 + "=" + encodeURIComponent(value));
                }
            }
            return queryString;
        }
        return buildQueryString.apply(void 0, __spreadArray([plainObject, ""], properties, false));
    };
    return Utils;
}());
export { Utils };
